'use client'

import { useMemo, useState } from 'react'
import { ArrowRight, Close as X } from '@carbon/icons-react'
import Link from 'next/link'

import { cn } from '@/lib/utils'
import { AnimatedNumber } from '@/components/ui/animated-number'

import {
  type Product,
  type UpdateType,
  VERSIONS,
  UPDATES,
  countByType,
} from './data'
import {
  HAIRLINE,
  RecordBand,
  ToggleChip,
  TYPE_META,
  UpdatesMasthead,
} from './update-kit'

/* ─── Release ledger ───────────────────────────────────────────────────────
   The page was a sidebar of black slab buttons beside a stack of grey cards,
   in a type scale a size or two below everything else on the site. It is now
   a ledger: version and date hang in a left margin, the release reads as a
   headline with its note under it, and hairlines carry the rhythm. No card
   shells, no tinted chips. ── */

function ReleaseRow({ version }: { version: (typeof VERSIONS)[number] }) {
  const counts = countByType(version.version)
  const total = Object.values(counts).reduce((a, b) => a + b, 0)
  const active = (
    Object.entries(counts) as [Exclude<UpdateType, 'All'>, number][]
  ).filter(([, n]) => n > 0)

  return (
    <Link
      href={`/product-updates/${version.version}`}
      className={cn(
        'group grid gap-x-10 gap-y-4 border-b py-8 sm:py-10',
        'sm:grid-cols-[9rem_minmax(0,1fr)_1.25rem]',
        HAIRLINE
      )}
    >
      <div className="sm:pt-1">
        <p className="font-mono text-lg font-semibold tabular-nums text-foreground sm:text-xl">
          {version.version}
        </p>
        <p className="mt-1.5 text-sm text-muted-foreground">
          {version.releaseDate}
        </p>
        <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
          {total} update{total !== 1 ? 's' : ''}
        </p>
      </div>

      <div>
        <h2 className="font-heading text-xl font-normal leading-snug text-foreground sm:text-2xl">
          {version.tagline}
        </h2>

        <p className="mt-3 max-w-3xl text-base leading-relaxed text-muted-foreground">
          {version.highlight}
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="text-muted-foreground/80">
            {version.products.join(' · ')}
          </span>
          {active.map(([type, count]) => {
            const { short, tone } = TYPE_META[type]
            return (
              <span key={type} className={cn('font-medium', tone)}>
                {count} {short.toLowerCase()}
              </span>
            )
          })}
        </div>
      </div>

      <ArrowRight
        aria-hidden
        className="hidden size-5 self-center text-muted-foreground/50 sm:block"
      />
    </Link>
  )
}

export default function ProductUpdatesPage() {
  const [productFilter, setProductFilter] = useState<Product>('All')
  const [typeFilter, setTypeFilter] = useState<UpdateType>('All')

  const products: Product[] = [
    'All',
    'UDC',
    'HRMS',
    'Payroll',
    'Accounts',
    'LMS',
    'SchoolERP',
  ]
  /* Only types that actually occur in the data get a chip. The full list was
     hardcoded here, but no release has shipped a 'Bug Fix' or 'Security'
     entry yet — so those two chips could only ever navigate to the empty
     state. When such entries land in data.ts the chips reappear on their
     own. */
  const types: UpdateType[] = useMemo(() => {
    const present = new Set(UPDATES.map((u) => u.type))
    return (
      ['All', 'New Feature', 'Improvement', 'Bug Fix', 'Security'] as const
    ).filter((t) => t === 'All' || present.has(t))
  }, [])

  const filteredVersions = useMemo(
    () =>
      VERSIONS.filter((v) =>
        UPDATES.some(
          (u) =>
            u.version === v.version &&
            (productFilter === 'All' || u.product === productFilter) &&
            (typeFilter === 'All' || u.type === typeFilter)
        )
      ),
    [productFilter, typeFilter]
  )

  const isFiltered = productFilter !== 'All' || typeFilter !== 'All'

  const totalUpdates = UPDATES.length
  const latest = VERSIONS[0]

  return (
    <main className="min-h-screen bg-background">
      <UpdatesMasthead
        eyebrow="Product updates"
        title="What's new at IntelQI"
        deck="Every release, every improvement, every fix documented in one place. Browse by version to see the full story of what changed and why."
      >
        <RecordBand
          fields={[
            {
              label: 'Releases',
              value: (
                <span className="font-mono tabular-nums">
                  <AnimatedNumber value={VERSIONS.length} />
                </span>
              ),
            },
            {
              label: 'Documented changes',
              value: (
                <span className="font-mono tabular-nums">
                  <AnimatedNumber value={totalUpdates} />
                </span>
              ),
            },
            {
              label: 'Latest',
              value: (
                <span className="font-mono">{latest?.version ?? 'N/A'}</span>
              ),
            },
            { label: 'Shipped', value: latest?.releaseDate ?? 'N/A' },
          ]}
        />
      </UpdatesMasthead>

      {/* ── Filters — a rail across the top instead of a sidebar, so the
          ledger below gets the full measure. ── */}
      <section className="container mx-auto">
        <div
          className={cn(
            'flex flex-col gap-5 border-b py-6 lg:flex-row lg:items-start lg:gap-12',
            HAIRLINE
          )}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="mr-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
              Product
            </span>
            {products.map((p) => (
              <ToggleChip
                key={p}
                active={productFilter === p}
                onClick={() => setProductFilter(p)}
              >
                {p}
                {p !== 'All' && (
                  <span className="text-xs tabular-nums text-muted-foreground/70">
                    {/* Same predicate the ledger filters by, scoped to this
                        product and honouring the active type filter — so the
                        number on the chip is exactly the release count that
                        clicking it will produce. The old badge counted
                        `v.products` metadata instead, and said "3" on chips
                        whose click yielded 1. */}
                    {
                      VERSIONS.filter((v) =>
                        UPDATES.some(
                          (u) =>
                            u.version === v.version &&
                            u.product === p &&
                            (typeFilter === 'All' || u.type === typeFilter)
                        )
                      ).length
                    }
                  </span>
                )}
              </ToggleChip>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <span className="mr-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
              Type
            </span>
            {types.map((t) => {
              const meta =
                t !== 'All' ? TYPE_META[t as Exclude<UpdateType, 'All'>] : null
              const Icon = meta?.icon
              return (
                <ToggleChip
                  key={t}
                  active={typeFilter === t}
                  onClick={() => setTypeFilter(t)}
                >
                  {Icon && <Icon className={cn('size-3.5', meta?.tone)} />}
                  {t}
                </ToggleChip>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── Ledger ── */}
      <section className="container mx-auto pb-20 sm:pb-28">
        <div
          className={cn(
            'flex items-center justify-between border-b py-4',
            HAIRLINE
          )}
        >
          <p className="text-sm text-muted-foreground">
            {filteredVersions.length} release
            {filteredVersions.length !== 1 ? 's' : ''}
            {isFiltered && ' matching current filters'}
          </p>

          {isFiltered && (
            <button
              type="button"
              onClick={() => {
                setProductFilter('All')
                setTypeFilter('All')
              }}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-3.5" /> Clear filters
            </button>
          )}
        </div>

        {filteredVersions.length === 0 ? (
          <div className="py-24 text-center">
            <p className="font-heading text-xl font-normal text-foreground sm:text-2xl">
              No releases match
            </p>
            <p className="mt-3 text-base text-muted-foreground">
              Try a different filter combination.
            </p>
            <button
              type="button"
              onClick={() => {
                setProductFilter('All')
                setTypeFilter('All')
              }}
              className="mt-6 text-sm font-medium text-foreground underline underline-offset-4 hover:no-underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div>
            {filteredVersions.map((v) => (
              <ReleaseRow key={v.version} version={v} />
            ))}
          </div>
        )}
      </section>
    </main>
  )
}
